import { useEffect, useState } from 'react';
import { useGetApiV1CourseregistrationSharedClassStudentList } from '@/shared/api/generated';
import type { RevisionDropdownOption } from '../types/revision';

/**
 * Busca server-side de turmas (ClassStudent) por nome para o filtro da revisão,
 * com debounce de 1s e mínimo de 3 caracteres. O `id` retornado é o
 * `classStudentId` esperado pelo grid.
 *
 * @param filter - Texto digitado no autocomplete (default: `''`)
 * @returns Turmas encontradas e o estado da query
 *
 * @example
 * ```tsx
 * const { classes, isQuerySuccess } = useRevisionClassStudentDropdown(search);
 * ```
 */
export function useRevisionClassStudentDropdown(filter = '') {
  const [debouncedFilter, setDebouncedFilter] = useState('');

  useEffect(() => {
    if (filter === '') {
      setDebouncedFilter('');
      return;
    }
    const timer = setTimeout(() => setDebouncedFilter(filter), 1000);
    return () => clearTimeout(timer);
  }, [filter]);

  const isReady = debouncedFilter.length >= 3;

  const { data, isLoading, isSuccess } = useGetApiV1CourseregistrationSharedClassStudentList(
    isReady ? { filter: debouncedFilter } : undefined,
    { query: { enabled: isReady } },
  );

  const classes: readonly RevisionDropdownOption[] = (data ?? []).map(item => ({
    id: String(item.key ?? ''),
    name: item.value ?? '',
  }));

  return { classes, isLoading, isQuerySuccess: isSuccess } as const;
}
